import { useInfiniteQuery } from "@tanstack/react-query";

import { keys } from "@/hooks/queries";
import { api, type NoteCardOut, type NoteCategory, type NoteSort, type NotesPage } from "@/lib/api";

import { PAGE_SIZE } from "./home-store";

export interface FeedFacets {
  query: string;
  sort: NoteSort;
  categories: NoteCategory[];
  folder: string;
}

function toSearchParams(facets: FeedFacets, cursor: string | null): string {
  const params = new URLSearchParams();
  if (facets.query.trim()) params.set("q", facets.query.trim());
  // Relevance only means something with a query; asking for it without one is
  // a 422 the server would be right to raise, so don't send it.
  const sort = facets.sort === "relevance" && !facets.query.trim() ? "recent" : facets.sort;
  params.set("sort", sort);
  for (const category of facets.categories) params.append("category", category);
  if (facets.folder) params.set("folder", facets.folder);
  params.set("limit", String(PAGE_SIZE));
  if (cursor) params.set("cursor", cursor);
  return params.toString();
}

/**
 * The homepage's card feed. Cursor-paginated: the server merges documents and
 * markdown notes into one keyset-ordered list, so "load more" can never skip or
 * repeat a note the way an offset over a live table would.
 *
 * A query with no explicit sort orders by relevance — typing into the search box
 * means "best matches first", not "newest first".
 */
export function useNotesFeed(facets: FeedFacets) {
  const effective: FeedFacets = {
    ...facets,
    sort: facets.query.trim() && facets.sort === "recent" ? "relevance" : facets.sort,
  };
  const facetKey = toSearchParams(effective, null);
  return useInfiniteQuery({
    queryKey: keys.notes(facetKey),
    queryFn: ({ pageParam }) =>
      api.request<NotesPage>("GET", `/api/notes?${toSearchParams(effective, pageParam)}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    // The feed is the landing surface; a stale-while-revalidate window keeps
    // returning to Home instant without serving genuinely old data.
    staleTime: 10_000,
  });
}

/** Flatten the paged response, de-duplicated defensively by id — a note created
 * while the user is paging could otherwise straddle two pages.
 *
 * `items` is read through a guard for the same reason the folder tree's is: this
 * is a PWA whose cached bundle can meet an API older or newer than it was built
 * against, and Home is the LANDING surface — a missing field must render an
 * empty feed, never take the whole panel down with "is not iterable". */
export function flattenFeed(pages: NotesPage[] | undefined): NoteCardOut[] {
  const seen = new Set<string>();
  const out: NoteCardOut[] = [];
  for (const page of pages ?? []) {
    for (const card of page?.items ?? []) {
      if (seen.has(card.id)) continue;
      seen.add(card.id);
      out.push(card);
    }
  }
  return out;
}

export interface DateGroup {
  /** ISO day key, for React keys and tests. */
  key: string;
  /** "August 14th" — the heading the brief specifies. */
  label: string;
  cards: NoteCardOut[];
}

const ORDINAL = (day: number): string => {
  if (day % 100 >= 11 && day % 100 <= 13) return "th";
  return ["th", "st", "nd", "rd"][day % 10] ?? "th";
};

/** "August 14th", or "Today"/"Yesterday" for the two days a user is most likely
 * to be looking for. The year is appended once it stops being obvious. */
export function formatDayLabel(date: Date, now: Date = new Date()): string {
  const day = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  if (day(date) === day(now)) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (day(date) === day(yesterday)) return "Yesterday";
  const month = date.toLocaleString(undefined, { month: "long" });
  const suffix = `${month} ${date.getDate()}${ORDINAL(date.getDate())}`;
  return date.getFullYear() === now.getFullYear() ? suffix : `${suffix}, ${date.getFullYear()}`;
}

/**
 * Group cards into date sections by CREATION date, preserving the order the
 * server returned within each group.
 *
 * Grouping happens here rather than server-side on purpose: the server
 * guarantees the ORDER, and the client owns the presentation, so changing a
 * facet re-groups instantly from data already in hand. It is only meaningful
 * for a chronological sort — an alphabetical or relevance-ordered list would
 * produce date headings that jump back and forth, so those render flat.
 */
export function groupByDay(cards: NoteCardOut[], now: Date = new Date()): DateGroup[] {
  const groups: DateGroup[] = [];
  let current: DateGroup | null = null;
  for (const card of cards) {
    const created = card.created_at ? new Date(card.created_at) : null;
    const valid = created && !Number.isNaN(created.getTime());
    const key = valid
      ? `${created.getFullYear()}-${created.getMonth() + 1}-${created.getDate()}`
      : "undated";
    if (!current || current.key !== key) {
      current = {
        key,
        label: valid ? formatDayLabel(created, now) : "No date",
        cards: [],
      };
      groups.push(current);
    }
    current.cards.push(card);
  }
  return groups;
}
